/**
 * Client API V2 — InfraNet GN (plan de mise en production §3, vague V1).
 *
 * Différences avec le client v1 (NetBox) :
 * - enveloppe unique `{data, meta, errors}` (V2 §4.1) — le client lit une
 *   réponse sans deviner sa forme ;
 * - concurrence optimiste : ETag dérivé de `row_version`, en-tête `If-Match`
 *   automatique sur toute modification, 412 géré par relance de lecture ;
 * - authentification par jeton porteur OIDC (PKCE), plus de token NetBox ;
 * - base `/api/v1/` (plus de `/api/plugins/fiberasset/`).
 */

const V2_BASE = "/api/v1";

/**
 * LA MÊME clé que dans `client.ts`. Il y a une identité, donc un jeton, donc un seul coffre.
 *
 * Deux clés distinctes — « infranet_v2_token » ici, « infranet_token » là-bas — donnaient une
 * application qui se croyait déconnectée juste après une connexion réussie : le retour de
 * Keycloak rangeait le jeton d'un côté, et `isAuthenticated()` le cherchait de l'autre. La
 * connexion aboutissait, et l'écran de connexion revenait quand même.
 *
 * Si cette valeur change, elle change AUX DEUX ENDROITS, ou nulle part.
 */
const TOKEN_KEY = "infranet_token";

// ─── Enveloppe V2 §4.1 ────────────────────────────────────────────────

export interface Envelope<T> {
  data: T;
  meta: {
    request_id: string;
    page?: number;
    page_size?: number;
    total?: number;
  };
  errors: ApiError[];
}

/**
 * Erreur telle que le socle la renvoie : `{code, champ, message}`. Les noms sont en français
 * parce que le gestionnaire d'erreurs V2 les produit ainsi ; lire `detail`/`field` ferait
 * afficher « HTTP 400 » sans jamais dire quel champ est en cause.
 */
export interface ApiError {
  code: string;
  champ: string | null;
  message: string;
}

export class V2ApiError extends Error {
  readonly status: number;
  readonly errors: ApiError[];
  readonly requestId: string;

  constructor(status: number, errors: ApiError[], requestId: string) {
    super(errors[0]?.message ?? `HTTP ${status}`);
    this.name = "V2ApiError";
    this.status = status;
    this.errors = errors;
    this.requestId = requestId;
  }

  get isForbidden(): boolean {
    return this.status === 401 || this.status === 403;
  }

  get isConflict(): boolean {
    return this.status === 412;
  }

  /** Erreurs replacées sur les champs, pour l'affichage champ par champ. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.errors) {
      if (e.champ) out[e.champ] = e.message;
    }
    return out;
  }
}

// ─── Gestion du jeton OIDC ────────────────────────────────────────────

function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function setV2Token(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}

export function clearV2Token(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

export function hasV2Token(): boolean {
  return getToken() !== null;
}

// ─── Requête générique ────────────────────────────────────────────────

/** Le même évènement que l'ancien client : deux noms feraient réagir l'application à l'un
 *  et pas à l'autre, selon l'écran qui a perdu la session. */
export const EVENEMENT_SESSION_PERDUE = "infranet:session-perdue";

export interface V2RequestOptions extends RequestInit {
  /** If-Match pour la concurrence optimiste (déduit de l'objet si absent). */
  ifMatch?: string;
  /** Ne pas attacher le jeton (pour la connexion elle-même). */
  anonymous?: boolean;
  /** Interne : la requête a déjà été rejouée après un renouvellement. Empêche la boucle. */
  dejaRejoue?: boolean;
}

async function v2Request<T>(path: string, options: V2RequestOptions = {}): Promise<Envelope<T>> {
  const { anonymous, ifMatch, dejaRejoue, ...init } = options;
  const token = anonymous ? null : getToken();

  // Un envoi de fichier ne porte PAS « application/json », et surtout pas un type multipart
  // écrit à la main : la limite entre les parties est tirée au sort par le navigateur, et
  // l'imposer soi-même produit un corps que le serveur ne sait pas découper. On laisse donc
  // `fetch` poser l'en-tête quand le corps est un FormData.
  const estFichier = init.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(estFichier ? {} : { "Content-Type": "application/json" }),
    ...(init.headers as Record<string, string>),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (ifMatch) {
    headers["If-Match"] = ifMatch;
  }

  const url = path.startsWith("http") ? path : `${V2_BASE}${path}`;
  const resp = await fetch(url, { ...init, headers });

  let body: Envelope<T>;
  try {
    body = (await resp.json()) as Envelope<T>;
  } catch {
    throw new V2ApiError(resp.status, [{ code: "parse", champ: null, message: resp.statusText }], "—");
  }

  if (!resp.ok) {
    // Un jeton expiré doit ARRÊTER la console, pas la faire tourner à vide.
    //
    // Les écrans V2 — carte, topologie, qualité, administration, import — passent tous par
    // ici, et ce chemin se contentait de lever. La carte, qui se rafraîchit toutes les
    // trente secondes, réinterrogeait donc indéfiniment avec un jeton mort : chaque appel
    // repartait en 401, les comptes retombaient à zéro, et l'écran affichait « aucun objet
    // dans ce périmètre ». Il accusait le référentiel d'être vide alors que la session était
    // finie. Constaté en production le 12/09/2026, sur une base fraîchement chargée de 2 279
    // objets.
    //
    // L'ancien client, lui, effaçait le jeton et prévenait l'application. Les deux chemins
    // font désormais la même chose : le jeton part, l'application revient à la connexion, et
    // Keycloak — dont la session vit plus longtemps — redonne un jeton sans rien demander.
    if (resp.status === 401 && !anonymous) {
      // D'abord RENOUVELER, et seulement si c'est impossible, rendre la main à la connexion.
      // Sans cette tentative, la session semblait durer quinze minutes alors que Keycloak la
      // tenait ouverte bien plus longtemps.
      if (!dejaRejoue) {
        const neuf = await renouvelerJeton();
        if (neuf) return v2Request<T>(path, { ...options, dejaRejoue: true });
      }
      clearV2Token();
      window.dispatchEvent(new CustomEvent(EVENEMENT_SESSION_PERDUE));
    }
    throw new V2ApiError(resp.status, body.errors ?? [], body.meta?.request_id ?? "—");
  }

  return body;
}

// ─── CRUD avec concurrence optimiste ──────────────────────────────────

/** Construit l'ETag depuis l'UUID et row_version d'une entité V2. */
export function etagOf(entity: { id: string; row_version: number }): string {
  return `W/"${entity.id}:${entity.row_version}"`;
}

export async function v2Get<T>(path: string): Promise<T> {
  const body = await v2Request<T>(path);
  return body.data;
}

export async function v2GetList<T>(
  path: string,
  params?: Record<string, string>,
): Promise<{ items: T[]; total: number }> {
  const qs = params ? `?${new URLSearchParams(params)}` : "";
  const body = await v2Request<T[]>(`${path}${qs}`);
  return {
    items: Array.isArray(body.data) ? body.data : [],
    total: body.meta.total ?? (Array.isArray(body.data) ? body.data.length : 0),
  };
}

export async function v2Post<T>(path: string, payload: unknown): Promise<T> {
  const body = await v2Request<T>(path, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return body.data;
}

/**
 * Envoi d'un fichier (import §18). Le corps part en multipart, sans en-tête écrit à la main.
 */
export async function v2PostFichier<T>(path: string, corps: FormData): Promise<T> {
  const body = await v2Request<T>(path, { method: "POST", body: corps });
  return body.data;
}

/**
 * PATCH avec If-Match automatique : la version est lue depuis l'objet
 * complet passé en argument. Le serveur répond 412 si quelqu'un d'autre
 * a écrit entre-temps — l'appelant recharge et rejoue.
 */
export async function v2Patch<T extends { id: string; row_version: number }>(
  path: string,
  entity: T,
  changes: Partial<T>,
): Promise<T> {
  try {
    const body = await v2Request<T>(path, {
      method: "PATCH",
      body: JSON.stringify(changes),
      ifMatch: etagOf(entity),
    });
    return body.data;
  } catch (err) {
    if (err instanceof V2ApiError && err.isConflict) {
      // 412 : relire l'état serveur et laisser l'appelant décider
      const fresh = await v2Get<T>(path);
      throw new ConflictError(fresh, err);
    }
    throw err;
  }
}

export class ConflictError<T> extends Error {
  readonly serverVersion: T;
  constructor(serverVersion: T, cause: V2ApiError) {
    super("Conflit de version : l'objet a été modifié par ailleurs. " +
      `Rechargez et rejouez (request_id: ${cause.requestId}).`);
    this.name = "ConflictError";
    this.serverVersion = serverVersion;
  }
}

// ─── OIDC PKCE (V2 §3.3 USR-001) ─────────────────────────────────────

/**
 * Fournisseur d'identité : `auth.infranetgn.com`, son nom public. Il n'y a pas de proxy
 * `/oidc/` sur la console — un chemin relatif renvoie 404. Le royaume déclare déjà
 * `app.infranetgn.com` en origine autorisée, donc l'appel direct passe sans réglage de plus.
 *
 * `VITE_OIDC_ISSUER` permet de viser un autre royaume en développement.
 */
const OIDC_ISSUER =
  import.meta.env?.VITE_OIDC_ISSUER ?? "https://auth.infranetgn.com/realms/infranet";
const OIDC_AUTH_ENDPOINT = `${OIDC_ISSUER}/protocol/openid-connect/auth`;
const OIDC_TOKEN_ENDPOINT = `${OIDC_ISSUER}/protocol/openid-connect/token`;
const OIDC_CLIENT_ID = "infranet-console";

function base64url(bytes: Uint8Array): string {
  const bin = String.fromCharCode(...bytes);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

/**
 * Les flots de connexion en attente, rangés par `state`.
 *
 * Le vérifiant PKCE vivait dans le `sessionStorage` de l'onglet qui lançait la connexion. Or le
 * retour de Keycloak n'arrive pas toujours dans cet onglet : lien d'e-mail d'une action
 * requise, page de connexion restée ouverte ailleurs, adresse de retour rouverte. Il trouvait
 * alors un stockage vide et répondait « vérifiant absent » à une connexion pourtant réussie
 * côté Keycloak (constaté le 13/09/2026).
 *
 * Le flot porte désormais un `state` aléatoire, que Keycloak renvoie tel quel (RFC 6749
 * §4.1.2). Le vérifiant est rangé sous ce `state`, dans un stockage partagé par les onglets,
 * et expire au bout de dix minutes. Le `state` sert aussi ce à quoi il est destiné : un retour
 * qui ne correspond à aucun flot lancé ici est refusé.
 */
const CLE_FLOTS_OIDC = "infranet_oidc_flots";
const DUREE_FLOT_MS = 10 * 60 * 1000;

interface FlotEnAttente {
  verifier: string;
  redirectUri: string;
  cree: number;
}

/** Le retour ne correspond à aucun flot connu : relancer la connexion est la seule issue. */
export class FlotOidcIntrouvable extends Error {}

function flotsEnAttente(): Record<string, FlotEnAttente> {
  const maintenant = Date.now();
  try {
    const flots = JSON.parse(localStorage.getItem(CLE_FLOTS_OIDC) ?? "{}") as Record<
      string,
      FlotEnAttente
    >;
    for (const [etat, flot] of Object.entries(flots)) {
      if (!flot?.verifier || maintenant - flot.cree > DUREE_FLOT_MS) delete flots[etat];
    }
    return flots;
  } catch {
    return {};
  }
}

function ranger(flots: Record<string, FlotEnAttente>): void {
  try {
    localStorage.setItem(CLE_FLOTS_OIDC, JSON.stringify(flots));
  } catch {
    // Stockage refusé par le navigateur : le retour ne trouvera pas son flot et le dira.
  }
}

/**
 * Démarre le flot OIDC Authorization Code + PKCE.
 * Redirige vers Keycloak — ne revient jamais ici.
 */
export async function startOIDCLogin(): Promise<void> {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
  const state = base64url(crypto.getRandomValues(new Uint8Array(16)));
  const challenge = await pkceChallenge(verifier);
  const redirectUri = `${window.location.origin}/callback`;

  const flots = flotsEnAttente();
  flots[state] = { verifier, redirectUri, cree: Date.now() };
  ranger(flots);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: OIDC_CLIENT_ID,
    redirect_uri: redirectUri,
    code_challenge: challenge,
    code_challenge_method: "S256",
    scope: "openid profile email",
    state,
  });

  window.location.href = `${OIDC_AUTH_ENDPOINT}?${params}`;
}

/** Un même code n'est échangé qu'une fois, même si l'écran de retour se monte deux fois. */
const echangesEnCours = new Map<string, Promise<string>>();

/**
 * Échange le code d'autorisation contre un jeton d'accès.
 * Appelé par la page de callback après retour de Keycloak.
 */
export function handleOIDCCallback(code: string, state: string | null): Promise<string> {
  const existant = echangesEnCours.get(code);
  if (existant) return existant;
  const echange = echangerCode(code, state);
  echangesEnCours.set(code, echange);
  return echange;
}

async function echangerCode(code: string, state: string | null): Promise<string> {
  const flots = flotsEnAttente();
  const flot = state ? flots[state] : undefined;
  if (!state || !flot) {
    ranger(flots);
    throw new FlotOidcIntrouvable(
      "Ce retour de connexion ne correspond à aucune connexion lancée depuis ce navigateur " +
        "dans les dix dernières minutes.",
    );
  }
  // Consommé AVANT l'échange : une adresse de retour rejouée ne peut pas réutiliser le flot.
  delete flots[state];
  ranger(flots);

  const resp = await fetch(OIDC_TOKEN_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      client_id: OIDC_CLIENT_ID,
      code_verifier: flot.verifier,
      redirect_uri: flot.redirectUri,
    }),
  });

  if (!resp.ok) {
    throw new Error(`Échange OIDC échoué : HTTP ${resp.status}`);
  }

  const tokens = (await resp.json()) as { access_token: string; refresh_token?: string };
  setV2Token(tokens.access_token);
  if (tokens.refresh_token) {
    sessionStorage.setItem(CLE_RAFRAICHISSEMENT, tokens.refresh_token);
  }
  return tokens.access_token;
}

const CLE_RAFRAICHISSEMENT = "infranet_v2_refresh";

/** Une seule tentative de renouvellement à la fois : dix requêtes qui expirent ensemble ne
 *  doivent pas déclencher dix échanges, dont neuf consommeraient un jeton déjà dépensé. */
let renouvellementEnCours: Promise<string | null> | null = null;

/**
 * Échange le jeton de rafraîchissement contre un jeton d'accès neuf.
 *
 * Keycloak en délivre un à la connexion, et la console le rangeait sans jamais s'en servir.
 * Le jeton d'accès vit quinze minutes : au-delà, chaque appel repartait en 401 et l'écran
 * revenait à la page de connexion. Trois fois en une heure de travail, alors que la session
 * Keycloak, elle, était toujours ouverte.
 *
 * Rend le nouveau jeton, ou `null` s'il n'y a rien à renouveler — auquel cas il faut vraiment
 * se reconnecter, et c'est l'appelant qui le décide.
 */
export async function renouvelerJeton(): Promise<string | null> {
  if (renouvellementEnCours) return renouvellementEnCours;

  const refresh = sessionStorage.getItem(CLE_RAFRAICHISSEMENT);
  if (!refresh) return null;

  renouvellementEnCours = (async () => {
    try {
      const resp = await fetch(OIDC_TOKEN_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          refresh_token: refresh,
          client_id: OIDC_CLIENT_ID,
        }),
      });
      if (!resp.ok) {
        // Le jeton de rafraîchissement est mort lui aussi : on le retire, sinon chaque
        // requête retentera un échange voué à l'échec.
        sessionStorage.removeItem(CLE_RAFRAICHISSEMENT);
        return null;
      }
      const tokens = (await resp.json()) as { access_token: string; refresh_token?: string };
      setV2Token(tokens.access_token);
      if (tokens.refresh_token) {
        sessionStorage.setItem(CLE_RAFRAICHISSEMENT, tokens.refresh_token);
      }
      return tokens.access_token;
    } catch {
      return null;
    } finally {
      renouvellementEnCours = null;
    }
  })();
  return renouvellementEnCours;
}

// ─── Endpoints métier ─────────────────────────────────────────────────

export interface MapFeature {
  type: "Feature";
  geometry: GeoJSON.Geometry;
  properties: Record<string, unknown>;
}

/**
 * Ce que le serveur renvoie réellement pour `/map/features`.
 *
 * Il renvoie `layers` — un dictionnaire de collections, une par couche — et JAMAIS une liste
 * `features` à plat. Le type déclarait pourtant `features`, et la carte dessinait donc
 * `undefined` : un écran vide, aucune erreur, et l'impression que le référentiel n'a rien
 * dedans. `tsc` ne pouvait rien voir, puisqu'il vérifiait le code contre ce type inventé.
 *
 * `layers` est conservé tel quel — l'ordre des couches est celui du dessin, le fond
 * administratif d'abord — et `features` est calculé à partir de lui pour les appelants qui
 * veulent tout d'un coup.
 */
/** Pourquoi une couche ne rend rien. Le serveur le sait ; l'écran ne peut pas le deviner. */
export interface LayerNote {
  /** `zoom` : masquée à cette échelle. `forbidden` : hors périmètre. `empty` : rien à montrer. */
  reason: "zoom" | "forbidden" | "empty";
  min_zoom?: number;
  message: string;
}

export interface MapFeaturesResponse {
  layers: Record<string, { type: "FeatureCollection"; features: MapFeature[] }>;
  counts: Record<string, number>;
  /** Une entrée par couche vide, avec la raison. Absent des serveurs antérieurs : facultatif. */
  layer_notes?: Record<string, LayerNote>;
  legend: {
    states: { code: string; label: string; halo: string; text: string }[];
    technologies: Record<string, string>;
    lifecycle: Record<string, { opacity: number; dash: number[] | null }>;
    reliability: Record<string, unknown>;
    /** Paliers de la couche « Risque ». Absent des serveurs antérieurs : facultatif. */
    risk_levels?: { code: string; label: string; color: string }[];
  };
  hypotheses: string[];
  generated_at: string;
  bbox: number[] | null;
  territory: string;
  zoom: number | null;
  /** Toutes les couches mises bout à bout, dans l'ordre de dessin. Calculé ici. */
  features: MapFeature[];
}

/**
 * Les couches que le serveur sait rendre, dans l'ordre où il les dessine, avec le nom qu'un
 * exploitant emploie. `territoires` est le fond administratif : il vient en premier et reste
 * sous tout le reste.
 */
export const COUCHES_CARTE: {
  cle: string;
  libelle: string;
  /** `false` : couche proposée mais non cochée à l'ouverture de la carte. */
  defaut?: boolean;
}[] = [
  { cle: "territoires", libelle: "Fond administratif" },
  { cle: "sites", libelle: "Sites" },
  { cle: "cables", libelle: "Câbles optiques" },
  { cle: "links", libelle: "Liens physiques" },
  { cle: "leased", libelle: "Liens loués" },
  { cle: "ptp", libelle: "Liens hertziens" },
  { cle: "bases", libelle: "Stations de base" },
  { cle: "sectors", libelle: "Secteurs radio" },
  { cle: "cpes", libelle: "Terminaux clients" },
  { cle: "incidents", libelle: "Incidents" },
  { cle: "supports", libelle: "Supports" },
  // Non cochée à l'ouverture : posé sur chaque site, le risque recouvrirait la lecture de
  // l'état. C'est une lecture qu'on choisit, pas un fond permanent.
  { cle: "risques", libelle: "Risque", defaut: false },
];

/** Les filtres que `/map/features` accepte. Tous facultatifs, tous combinables (GEO-004). */
export interface MapQuery {
  /** Couches demandées. Vide ou absent : toutes. */
  layers?: string[];
  /** Emprise « lon_min,lat_min,lon_max,lat_max ». */
  bbox?: string;
  /** Niveau de zoom : le serveur s'en sert pour alléger le fond administratif. */
  zoom?: number;
  /** Chemin d'habilitation, « GN/GN-BOK ». Le serveur borne au sous-arbre. */
  territory?: string;
  /** État opérationnel constaté : up, degraded, down, unknown. */
  status?: string;
}

export async function getMapFeatures(requete: MapQuery | string = {}): Promise<MapFeaturesResponse> {
  // L'ancienne signature prenait une emprise seule. Les appels existants continuent de
  // fonctionner : c'est la seule raison pour laquelle la chaîne est encore acceptée ici.
  const q: MapQuery = typeof requete === "string" ? { bbox: requete } : requete;
  const params = new URLSearchParams();
  if (q.layers?.length) params.set("layers", q.layers.join(","));
  if (q.bbox) params.set("bbox", q.bbox);
  if (q.zoom != null) params.set("zoom", String(Math.round(q.zoom * 10) / 10));
  if (q.territory) params.set("territory", q.territory);
  if (q.status) params.set("status", q.status);
  const qs = params.toString() ? `?${params}` : "";
  const brut = await v2Get<Omit<MapFeaturesResponse, "features">>(`/map/features${qs}`);
  return {
    ...brut,
    features: Object.values(brut.layers ?? {}).flatMap((couche) => couche?.features ?? []),
  };
}

/** Un objet trouvé autour d'un point, avec sa distance au centre (GEO-015). */
export interface ProximityItem {
  type: string;
  id: string;
  code: string;
  distance_m: number;
  name?: string;
  [autre: string]: unknown;
}

export interface ProximityResponse {
  center: { lon: number; lat: number };
  radius_m: number;
  items: ProximityItem[];
  /** Ce que le serveur n'a PAS pu évaluer, faute de droits. À afficher, jamais à masquer. */
  hypotheses: string[];
}

export async function getProximity(
  lon: number,
  lat: number,
  rayonM = 500,
): Promise<ProximityResponse> {
  const params = new URLSearchParams({
    lon: String(lon),
    lat: String(lat),
    radius_m: String(Math.round(rayonM)),
  });
  return v2Get<ProximityResponse>(`/geo/proximity?${params}`);
}

/** Une modification enregistrée sur un objet (GEO-013). */
export interface HistoryEvent {
  at: string;
  action: string;
  actor: string | null;
  reason: string;
  request_id: string;
  geometry_changed: boolean;
}

export interface HistoryResponse {
  object_type: string;
  object_id: string;
  events: HistoryEvent[];
}

export async function getHistory(objectType: string, id: string): Promise<HistoryResponse> {
  return v2Get<HistoryResponse>(`/geo/history/${objectType}/${id}`);
}

/** Une entrée trouvée. `/search` groupe par type : voir `SearchGroup`. */
export interface SearchEntry {
  id: string;
  code: string;
  label: string;
  territory: string | null;
  position: { lon: number; lat: number } | null;
}

/** Un groupe de résultats. Le serveur cherche chaque type dans le périmètre autorisé. */
export interface SearchGroup {
  type: string;
  label: string;
  more: boolean;
  items: SearchEntry[];
}

export interface SearchResponse {
  query: string;
  groups: SearchGroup[];
  total: number;
  hypotheses: string[];
}

/**
 * Recherche globale. La route est `/search` (montée à la racine de l'API), et la réponse
 * n'est PAS une liste paginée : elle groupe par type d'objet, chaque type étant cherché dans
 * le périmètre où le compte a le droit de le lire. Aplatir ces groupes ferait perdre
 * l'information la plus utile de l'écran : de quel type est ce qu'on a trouvé.
 */
export async function globalSearch(query: string): Promise<SearchResponse> {
  return v2Get<SearchResponse>(`/search?q=${encodeURIComponent(query)}`);
}

// ── Jumeau numérique : le graphe de dépendances (CDC V2.5 §5.9) ───────────────────────────

/** Un objet atteint par le parcours, avec la règle qui l'a désigné. */
export interface GraphNode {
  type: string;
  id: string;
  label: string;
  kind: string;
  rule: string;
  depth: number;
  territory_path: string;
}

export interface ImpactResponse {
  object_type: string;
  object_id: string;
  impacted: GraphNode[];
  /** Les chemins de SECOURS. Ils ne tombent pas : ils protègent. Jamais parmi les touchés. */
  protected: { type: string; id: string; label: string; secouru_par: string }[];
  counts: Record<string, number>;
  hypotheses: string[];
}

export interface DependenciesResponse {
  object_type: string;
  object_id: string;
  depends_on: GraphNode[];
  counts: Record<string, number>;
  hypotheses: string[];
}

export interface GraphState {
  total_edges: number;
  edges_by_rule: Record<string, number>;
  edges_by_kind: Record<string, number>;
  last_built_at: string | null;
  depth_max: number;
  entry_points: { type: string; id: string; label: string; direct_dependents: number }[];
}

export function getGraphState(): Promise<GraphState> {
  return v2Get<GraphState>("/intelligence/graph");
}

export function getImpact(type: string, id: string): Promise<ImpactResponse> {
  return v2Get<ImpactResponse>(
    `/intelligence/impact?object_type=${encodeURIComponent(type)}&object_id=${encodeURIComponent(id)}`,
  );
}

export function getDependencies(type: string, id: string): Promise<DependenciesResponse> {
  return v2Get<DependenciesResponse>(
    `/intelligence/dependencies?object_type=${encodeURIComponent(type)}&object_id=${encodeURIComponent(id)}`,
  );
}

export interface CapacitePerdue {
  mbps: number;
  objects: number;
  /** Objets tombés de cette nature qui ne déclarent aucun débit : le total les ignore. */
  without_value: number;
}

/** Bilan d'une ou plusieurs pannes simultanées (`intelligence/impact-assessment`). */
export interface ImpactAssessment {
  failures: { type: string; id: string; label: string }[];
  impacted: GraphNode[];
  counts: Record<string, number>;
  terminals: { total: number; radio: number; optical: number };
  customers: { references: number; from_services: number; from_terminals: number; sample: string[] };
  services: {
    id: string;
    code: string;
    name: string;
    customer: string;
    subscribed_mbps: number;
    sla_ref: string;
    criticality: string;
  }[];
  sites: { type: string; id: string; label: string }[];
  lost_capacity: Record<string, CapacitePerdue>;
  max_criticality: string | null;
  severity: { proposed: "critical" | "major" | "minor"; reasons: string[]; rule: string[] };
  restoration: {
    estimable: boolean;
    median_minutes: number | null;
    min_minutes: number | null;
    max_minutes: number | null;
    sample_size: number;
    reason: string;
  };
  open_incidents: {
    id: string;
    business_code: string;
    title: string;
    severity: string;
    status: string;
    sla_due_at: string | null;
  }[];
  /** Chemins couverts par un secours qui SURVIT à la panne : ils ne sont pas comptés touchés. */
  protected: { type: string; id: string; label: string; backed_up_by: string[] }[];
  /** Chemins dont tous les secours sont tombés avec eux. */
  lost_backups: { type: string; id: string; label: string; backups: string[] }[];
  hypotheses: string[];
  complete: boolean;
}

export interface ImpactRunResume {
  id: string;
  object_type: string;
  object_id: string;
  object_label: string;
  origin: string;
  depth: number;
  result: unknown;
  computed_by: string;
  computed_at: string;
}

export function getImpactAssessment(objets: string[]): Promise<ImpactAssessment> {
  return v2Get<ImpactAssessment>(
    `/intelligence/impact-assessment?objects=${encodeURIComponent(objets.join(","))}`,
  );
}

export function archiverImpact(objets: string[]): Promise<ImpactRunResume> {
  return v2Post<ImpactRunResume>("/intelligence/impact-assessment", { objects: objets });
}

export function getImpactRuns(): Promise<ImpactRunResume[]> {
  return v2Get<ImpactRunResume[]>("/intelligence/impact-runs");
}

// ─── Centre de commandement (`reporting/command-center`) ─────────────

/** Une section que le compte ne peut pas lire porte sa raison au lieu de ses données. */
export type SectionRefusee = { available: false; reason: string };
type Section<T> = ({ available: true } & T) | SectionRefusee;

export type EtatReseau = "up" | "degraded" | "down" | "unknown";

export interface CommandCenter {
  territory: string;
  computed_at: string;
  network: Section<{
    families: { key: string; label: string; total: number; by_state: Record<EtatReseau, number> }[];
    totals: Record<EtatReseau, number>;
  }>;
  incidents: Section<{
    open: number;
    by_severity: Record<"critical" | "major" | "minor", number>;
    sla_overdue: number;
    /** `null` quand le compte lit les comptes mais pas les incidents eux-mêmes. */
    items:
      | {
          id: string;
          business_code: string;
          title: string;
          severity: string;
          status: string;
          detected_at: string;
          sla_due_at: string | null;
          sla_overdue: boolean;
        }[]
      | null;
    items_reason: string;
  }>;
  risk: Section<{
    scored: number;
    by_level: Record<"faible" | "moyen" | "eleve" | "critique", number>;
    computed_at: string | null;
    top: {
      subject_type: string;
      subject_id: string;
      label: string;
      score: number;
      level: string;
      /** `null` : une seule trace, pas encore de point de comparaison — ce n'est pas « stable ». */
      trend: number | null;
      factor: string;
    }[];
  }>;
  capacity: Section<{
    subjects: number;
    measured: number;
    above_attention: number;
    above_action: number;
    thresholds_pct: [number, number];
    top: {
      subject_type: string;
      subject_id: string;
      label: string;
      utilization_pct: number;
      load_mbps: number;
      capacity_mbps: number;
      measured: boolean;
    }[];
  }>;
  energy: Section<{ sites: number; without_source: number; examples: string[] }>;
  collections: Section<{
    connectors: {
      code: string;
      name: string;
      adapter: string;
      active: boolean;
      last_run_at: string | null;
      error: string | null;
      recorded: number | null;
    }[];
  }>;
  quality: Section<{
    completeness_pct: number | null;
    anomalies: number;
    by_rule: Record<string, number>;
  }>;
  hypotheses: string[];
}

/**
 * Les territoires des rôles EFFECTIFS du compte, du plus large au plus étroit. Le centre de
 * commandement s'ouvre sur le pays ; un chef d'agence y est refusé, et doit retomber sur son
 * agence plutôt que sur un écran vide.
 */
export async function getMesTerritoires(): Promise<string[]> {
  const moi = await v2Get<{ roles: { territory_path: string; effective: boolean }[] }>("/iam/me");
  const chemins = new Set(moi.roles.filter((r) => r.effective).map((r) => r.territory_path));
  return [...chemins].sort((a, b) => a.split("/").length - b.split("/").length || a.localeCompare(b));
}

export function getCommandCenter(territoire?: string | null): Promise<CommandCenter> {
  const qs = territoire ? `?territory=${encodeURIComponent(territoire)}` : "";
  return v2Get<CommandCenter>(`/reporting/command-center${qs}`);
}
